import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid } from "antd";
import { ConvertProposals, ProposalModel } from "../../proposal/model/proposals.ui";
import { proposalApi } from "../../proposal/api/proposalApi";
import { PAGE_SIZE } from "../../proposal/components/proposalHelpers";
import { ProposalListPage } from "../../proposal/pages/ProposalListPage";
import { PATHS } from "../../../app/router/paths";

const { useBreakpoint } = Grid;

interface ProposalListCache {
  proposals: ProposalModel[];
  page: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  searchText: string;
  statusFilter: string;
  dateRange: [string, string] | null;
}

let cachedProposalList: ProposalListCache | null = null;

export const ProposalView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReload =
    (location.state as { reload?: boolean } | null)?.reload === true;

  const screens = useBreakpoint();
  const isDesktop = !!screens.md;

  const [proposals, setProposals] = useState<ProposalModel[]>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.proposals;
    return [];
  });
  const [page, setPage] = useState<number>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.page;
    return 1;
  });
  const [totalPages, setTotalPages] = useState<number>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.totalPages;
    return 1;
  });
  const [totalItems, setTotalItems] = useState<number>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.totalItems;
    return 0;
  });
  const [hasMore, setHasMore] = useState<boolean>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.hasMore;
    return true;
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!shouldReload && cachedProposalList) return false;
    return true;
  });
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.searchText;
    return "";
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.statusFilter;
    return "all";
  });
  const [dateRange, setDateRange] = useState<[string, string] | null>(() => {
    if (!shouldReload && cachedProposalList) return cachedProposalList.dateRange;
    return null;
  });

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateCache = useCallback((updated: Partial<ProposalListCache>) => {
    cachedProposalList = {
      proposals: updated.proposals ?? cachedProposalList?.proposals ?? [],
      page: updated.page ?? cachedProposalList?.page ?? 1,
      totalPages: updated.totalPages ?? cachedProposalList?.totalPages ?? 1,
      totalItems: updated.totalItems ?? cachedProposalList?.totalItems ?? 0,
      hasMore: updated.hasMore ?? cachedProposalList?.hasMore ?? true,
      searchText: updated.searchText ?? cachedProposalList?.searchText ?? "",
      statusFilter: updated.statusFilter ?? cachedProposalList?.statusFilter ?? "all",
      dateRange: updated.dateRange !== undefined ? updated.dateRange : (cachedProposalList?.dateRange ?? null),
    };
  }, []);

  const fetchProposals = useCallback(
    async (
      targetPage: number,
      isRefresh = false,
      searchParam = searchText,
      statusParam = statusFilter,
      dateRangeParam = dateRange,
      append = false
    ) => {
      if (isRefresh) {
        setIsRefreshing(true);
      } else if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const searchArg = searchParam.trim() || undefined;
        const statusArg = statusParam !== "all" ? statusParam : undefined;
        const effectiveFromArg = dateRangeParam && dateRangeParam[0] ? `${dateRangeParam[0]}T00:00:00` : undefined;
        let effectiveToArg = dateRangeParam && dateRangeParam[1] ? `${dateRangeParam[1]}T23:59:59` : undefined;
        if (effectiveToArg !== undefined && effectiveFromArg !== undefined && effectiveToArg <= effectiveFromArg) {
          effectiveToArg = undefined;
        }

        const response = await proposalApi.getProposals(
          targetPage,
          PAGE_SIZE,
          searchArg,
          statusArg,
          undefined,
          undefined,
          effectiveFromArg,
          effectiveToArg
        );
        const mappedItems = ConvertProposals(response?.items || []);
        const pages = response?.pagination?.total_pages ?? 1;
        const total = response?.pagination?.total ?? mappedItems.length;
        const newHasMore = targetPage < pages && mappedItems.length > 0;

        setProposals((prev) => {
          const newProposals = append ? [...prev, ...mappedItems] : mappedItems;
          updateCache({
            proposals: newProposals,
            page: targetPage,
            totalPages: pages,
            totalItems: total,
            hasMore: newHasMore,
            searchText: searchParam,
            statusFilter: statusParam,
            dateRange: dateRangeParam,
          });
          return newProposals;
        });
        setPage(targetPage);
        setTotalPages(pages);
        setTotalItems(total);
        setHasMore(newHasMore);
      } catch (error) {
        console.error("Failed to fetch proposals:", error);
      } finally {
        setIsRefreshing(false);
        setIsLoadingMore(false);
        setIsLoading(false);
      }
    },
    [searchText, statusFilter, dateRange, updateCache]
  );

  useEffect(() => {
    if (shouldReload || !cachedProposalList) {
      cachedProposalList = null;
      window.history.replaceState({}, document.title);
      fetchProposals(1, false, "", "all", null);
    }
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [shouldReload]);

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchProposals(1, false, val, statusFilter, dateRange);
    }, 350);
  };

  const handleStatusFilterChange = (val: string) => {
    setStatusFilter(val);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    fetchProposals(1, false, searchText, val, dateRange);
  };

  const handleDateRangeChange = (dates: [string, string] | null) => {
    setDateRange(dates);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    fetchProposals(1, false, searchText, statusFilter, dates);
  };

  const handleRefresh = async () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    await fetchProposals(1, true, searchText, statusFilter, dateRange);
  };

  const handleLoadMore = async () => {
    if (isLoadingMore || isRefreshing || !hasMore) return;
    await fetchProposals(page + 1, false, searchText, statusFilter, dateRange, true);
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage === page || newPage < 1 || newPage > totalPages) return;
    await fetchProposals(newPage, false, searchText, statusFilter, dateRange, false);
  };

  const handleOpenDetail = (record: ProposalModel) => {
    if (record.propose_id) {
      navigate(PATHS.PROPOSAL_DETAIL.replace(":proposalId", String(record.propose_id)), {
        state: { proposal: record },
      });
    }
  };

  return (
    <ProposalListPage
      proposals={proposals}
      isDesktop={isDesktop}
      onSelectProposal={handleOpenDetail}
      onRefresh={handleRefresh}
      onLoadMore={handleLoadMore}
      onPageChange={handlePageChange}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      currentPage={page}
      totalPages={totalPages}
      totalItems={totalItems}
      searchText={searchText}
      onSearchChange={handleSearchChange}
      statusFilter={statusFilter}
      onStatusFilterChange={handleStatusFilterChange}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
    />
  );
};

export default ProposalView;
